/**
 * XML Block Import Service
 * Handles the import of XML blocks to standalone blocks via API
 */

import { createStandaloneBlock, updateMetaTags, patchComplexBlockDirect, translateStandaloneBlock, getStandaloneBlock, sendToTesting, moveToQaComplete, publishDesign, fetchPublishedBlocksByMetadataFilter } from './services/complexDocumentsAPI';
import { extractUnsupportedCharacters } from './helper';
import { HTML_BLOCK, COMPLEX_DOCUMENT_VERSION, DESIGN_TYPE } from './complexDocumentConstants';
import {
  parseXMLBlocks,
  getBlockName,
  sanitizeBlockName,
  getAvailableLanguages,
  getLanguageContent,
  containsUnsupportedTags,
  wrapPlainTextInParagraph,
  splitBrIntoParagraphs,
} from './xmlBlockImporter';
import { DUPLICATE_IYTXTBS } from './duplicateIytxtbsList';

const CONCURRENCY_LIMIT = 10;

/**
 * Process an array of items with a limited number of concurrent async workers.
 * @param {Array} items - Items to process
 * @param {number} concurrency - Max parallel workers
 * @param {Function} processFn - Async function(item, index)
 */
const processWithConcurrency = async (items, concurrency, processFn) => {
  let index = 0;
  const runNext = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      await processFn(items[currentIndex], currentIndex);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => runNext()),
  );
};

/**
 * Run async work with a global cap (many block workers share one limiter for updateMetaTags).
 * @param {number} limit - Max concurrent executions
 * @returns {(fn: () => Promise<unknown>) => Promise<unknown>}
 */
const createLimitedRunner = (limit) => {
  let active = 0;
  const queue = [];
  const runNext = () => {
    if (active >= limit || queue.length === 0) return;
    active += 1;
    const { fn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(fn)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        runNext();
      });
  };
  return (fn) =>
    new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      runNext();
    });
};

/**
 * Only one "new standalone block group" flow may run at a time: first-language create plus
 * that group's metadata. Parallel new-group creates have been observed to receive the same
 * groupId from the API; keeping metadata in the same critical section preserves ordering.
 */
const runNewStandaloneGroup = createLimitedRunner(1);

/**
 * Pre-scan all blocks to resolve final block names, handling duplicate scenarios
 * uniformly for both iytxtbs-based and nameFr/nameNl-based blocks:
 *
 * - Base name is iytxtbs (when present) or nameFr / nameNl (fallback).
 * - When a base name appears in more than one block within the file, OR
 *   already exists among published blocks, povc is prepended to ALL blocks
 *   sharing that base name so that cross-povc entries become distinct.
 * - Any remaining same-povc collisions are handled downstream by
 *   getUniqueBlockName (_1, _2, ...).
 *
 * @param {Array} blocks - All parsed block objects
 * @param {Set<string>} existingNames - Published block names (parentBlockName format)
 * @returns {string[]} Resolved block names indexed by block position
 */
const SUPPORTED_LANGUAGES = ['fr', 'nl', 'en', 'de'];

const resolveBlockNames = (blocks, existingNames) => {
  // Case-insensitive lowercase lookup for existing names
  const existingNamesLower = new Set();
  if (existingNames && existingNames.size > 0) {
    for (const name of existingNames) {
      existingNamesLower.add(name.toLowerCase());
    }
  }

  // Groups keyed by lowercased sanitized name for case-insensitive collision detection.
  // iytxtbsGroups  -> blocks that have a non-empty iytxtbs
  // nameGroups     -> blocks that fall back to nameFr / nameNl
  const iytxtbsGroups = {};
  const nameGroups = {};
  // Unified map: lowercased sanitized name -> all block indices regardless of source field.
  // Used to detect cross-map collisions (e.g. iytxtbs "AVENANT" vs nameFr "avenant").
  const unifiedGroups = {};

  blocks.forEach((block, idx) => {
    if (block.iytxtbs && block.iytxtbs.trim() !== '') {
      const raw = block.iytxtbs.trim();
      const lowerKey = sanitizeBlockName(raw).toLowerCase();
      if (!iytxtbsGroups[lowerKey]) iytxtbsGroups[lowerKey] = [];
      iytxtbsGroups[lowerKey].push(idx);
      if (!unifiedGroups[lowerKey]) unifiedGroups[lowerKey] = [];
      unifiedGroups[lowerKey].push(idx);
    } else {
      let baseName = 'Unnamed Block';
      if (block.nameFr && block.nameFr.trim() !== '') {
        baseName = block.nameFr.trim();
      } else if (block.nameNl && block.nameNl.trim() !== '') {
        baseName = block.nameNl.trim();
      }
      const lowerKey = sanitizeBlockName(baseName).toLowerCase();
      if (!nameGroups[lowerKey]) nameGroups[lowerKey] = [];
      nameGroups[lowerKey].push(idx);
      if (!unifiedGroups[lowerKey]) unifiedGroups[lowerKey] = [];
      unifiedGroups[lowerKey].push(idx);
    }
  });

  // A lowercased sanitized name is a "duplicate" when it appears more than once
  // across ALL blocks (regardless of whether it came from iytxtbs or nameFr/nameNl),
  // OR it collides with an already-published name.
  const duplicateKeys = new Set();

  for (const [lowerKey, indices] of Object.entries(unifiedGroups)) {
    if (indices.length > 1) {
      duplicateKeys.add(lowerKey);
    }
  }

  // Also treat as duplicate if any language variant already exists among published blocks
  if (existingNamesLower.size > 0) {
    for (const lowerKey of Object.keys(unifiedGroups)) {
      if (duplicateKeys.has(lowerKey)) continue;
      const collidesWithExisting = SUPPORTED_LANGUAGES.some(
        (lang) => existingNamesLower.has(`${lowerKey}_${lang}`),
      );
      if (collidesWithExisting) {
        duplicateKeys.add(lowerKey);
      }
    }
  }

  return blocks.map((block) => {
    if (block.iytxtbs && block.iytxtbs.trim() !== '') {
      const raw = block.iytxtbs.trim();
      const lowerKey = sanitizeBlockName(raw).toLowerCase();
      if (duplicateKeys.has(lowerKey) && block.povc && block.povc.trim() !== '') {
        return sanitizeBlockName(`${block.povc}-${raw}`);
      }
      return sanitizeBlockName(raw);
    }

    let baseName = 'Unnamed Block';
    if (block.nameFr && block.nameFr.trim() !== '') {
      baseName = block.nameFr.trim();
    } else if (block.nameNl && block.nameNl.trim() !== '') {
      baseName = block.nameNl.trim();
    }

    const lowerKey = sanitizeBlockName(baseName).toLowerCase();
    if (duplicateKeys.has(lowerKey) && block.povc && block.povc.trim() !== '') {
      return sanitizeBlockName(`${block.povc}-${baseName}`);
    }
    return sanitizeBlockName(baseName);
  });
};

/**
 * Create payload for standalone block
 * @param {Object} block - Block object from XML
 * @param {string} language - Language code
 * @param {number} resourceGroupId - Resource group ID
 * @param {number} categoryId - Category ID
 * @param {string} groupId - Group ID for related blocks (optional)
 * @returns {Object} Payload for createStandaloneBlock API
 */
const createBlockPayload = (block, language, resourceGroupId, categoryId, groupId = '', parentBlockName, resolvedBlockName) => {
  const blockName = resolvedBlockName || getBlockName(block);
  let htmlContent = getLanguageContent(block, language);
  
  // Apply content transformations: wrap plain text in <p>, split <br> into separate paragraphs
  if (htmlContent) {
    htmlContent = wrapPlainTextInParagraph(htmlContent);
    htmlContent = splitBrIntoParagraphs(htmlContent);
  }
  
  // Default HTML content if none provided
  const defaultHtml = '<p style="text-align: left"> <br/> </p>';
  const content = htmlContent || defaultHtml;
  
  // Determine block type based on category
  const blockType = block.category && block.category.toLowerCase().includes('footer')
    ? 'footer'
    : block.category.toLowerCase().includes('header')
      ? 'header'
      : 'content';
  const htmlVariableContractName = blockName.replace(/[^a-zA-Z0-9-]/g, '-').trim(); // Replace special characters with "-"
  
  return {
    categoryId: categoryId,
    resourceGroupId,
    parentBlockName: parentBlockName || `${blockName}_${language}`,
    isReferenced: true,
    blocks: [
      {
        blockType: HTML_BLOCK,
        contents: content,
        orderNumber: 1,
        style: { height: 100, lineSpacing: 1.1, paragraphSpacing: 10, alignment: 'justify' },
        isEditable: true, // To allow default contents
        variableContract: [{ name: `paragraph-${htmlVariableContractName}`, type: 'html' }],
      },
    ],
    type: blockType,
    language,
    style: {
      fontFamily: 'Arial',
      fontSize: 13,
      lineSpacing: 1.1
    },
    ...(groupId && { groupId }),
  };
};

/**
 * Publish a block through the approval lifecycle
 * @param {string} blockId - Block ID to publish
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Object>} Publishing result with status
 */
const publishBlock = async (blockId, onProgress) => {
  try {
    // Step 1: Move to QA Ready
    if (onProgress) {
      onProgress({
        publishStage: 'qa-ready',
        blockId,
        publishStatus: 'qa-ready',
      });
    }
    
    const qaReadyResponse = await sendToTesting(blockId, DESIGN_TYPE.BLOCK);
    const successStatusCodes = [2601, 728, 1904];
    
    if (!successStatusCodes.includes(qaReadyResponse?.statusCode)) {
      const errorMessage = qaReadyResponse?.errors?.[0]?.description || 'Failed to move to QA Ready';
      return {
        success: false,
        stage: 'qa-ready',
        errorMessage,
        blockId,
      };
    }

    // Step 2: Move to QA Complete
    if (onProgress) {
      onProgress({
        publishStage: 'qa-complete',
        blockId,
        publishStatus: 'qa-complete',
      });
    }
    
    const qaCompleteResponse = await moveToQaComplete(blockId, DESIGN_TYPE.BLOCK);
    if (qaCompleteResponse?.errors && qaCompleteResponse.errors.length > 0) {
      const errorMessage = qaCompleteResponse.errors[0]?.description || 'Failed to move to QA Complete';
      return {
        success: false,
        stage: 'qa-complete',
        errorMessage,
        blockId,
      };
    }

    // Step 3: Publish
    if (onProgress) {
      onProgress({
        publishStage: 'published',
        blockId,
        publishStatus: 'published',
      });
    }
    
    const publishResponse = await publishDesign(blockId, DESIGN_TYPE.BLOCK);
    if (publishResponse?.errors && publishResponse.errors.length > 0) {
      const errorMessage = publishResponse.errors[0]?.description || 'Failed to publish';
      return {
        success: false,
        stage: 'published',
        errorMessage,
        blockId,
      };
    }

    return {
      success: true,
      stage: 'published',
      blockId,
    };
  } catch (error) {
    console.error('Publishing error:', error);
    return {
      success: false,
      stage: 'error',
      errorMessage: error.message || 'Unknown publishing error',
      blockId,
    };
  }
};

/**
 * Create metadata payload for updateMetaTags
 * @param {Object} block - Block object from XML
 * @returns {Array} Array of metadata objects
 */
const createMetadataPayload = (block) => {
  const metadata = [];
  metadata.push({ key: "type", value: "paragraph" });
  
  if (block.povc && block.povc.trim() !== '') {
    metadata.push({ key: 'povc', value: block.povc.trim() });
  }
  
  if (block.iytxtbs && block.iytxtbs.trim() !== '') {
    metadata.push({ key: 'iytxtbs', value: block.iytxtbs.trim() });
  }
  
  if (block.category && block.category.trim() !== '') {
    metadata.push({ key: 'category', value: block.category.trim() });
  }

  if (block.nameFr && block.nameFr.trim() !== '') {
    metadata.push({ key: 'nameFr', value: block.nameFr });
  }

  if (block.nameNl && block.nameNl.trim() !== '') {
    metadata.push({ key: 'nameNl', value: block.nameNl });
  }

  if (block.editable && block.editable.trim() !== '') {
    metadata.push({ key: 'editable', value: block.editable });
  }

  // Add filename with extension to metadata - TODO figure out a way to remove this metadata from the block after the import
  if (block.filename && block.filename.trim() !== '') {
    const filenameWithExtension = block.filename.trim().split('/').pop();
    metadata.push({ key: 'filename', value: filenameWithExtension });
  }
  
  return metadata;
};

const applyMetadataToImportedBlockGroup = async (groupId, metadata, blockIndex, blockName) => {
  try {
    const metaResponse = await updateMetaTags('block-document', groupId, metadata);
    if (!metaResponse || (metaResponse.statusCode != null && metaResponse.statusCode >= 400)) {
      console.warn(
        `Failed to update metadata for block index ${blockIndex} (${blockName}):`,
        metaResponse,
      );
    }
  } catch (metaError) {
    console.warn(`Failed to update metadata for block index ${blockIndex} (${blockName}):`, metaError);
  }
};

/**
 * Build a Set of existing published block names (parentBlockName pattern: "name_lang")
 * so that newly imported blocks can be deduplicated against them.
 */
const buildExistingBlockNameCache = async (onProgress) => {
  const nameSet = new Set();

  if (onProgress) {
    onProgress({
      stage: 'building-name-cache',
      message: 'Fetching existing published blocks to check for name collisions...',
    });
  }

  const criteria = [{ key: 'type', value: 'paragraph' }];
  const existingBlocks = await fetchPublishedBlocksByMetadataFilter(
    criteria,
    '',
    '',
    0,
    18000,
    (progress) => {
      if (onProgress) {
        onProgress({
          stage: 'building-name-cache',
          page: progress.page,
          totalFetched: progress.totalFetched,
        });
      }
    },
  );

  for (const block of existingBlocks) {
    if (block.name) {
      nameSet.add(block.name.toLowerCase());
    }
  }

  if (onProgress) {
    onProgress({
      stage: 'name-cache-complete',
      message: `Loaded ${nameSet.size} existing block names`,
      existingNameCount: nameSet.size,
    });
  }

  return nameSet;
};

/**
 * Return a unique block name by appending _1, _2, ... if the base name already exists.
 * Registers the chosen name into usedNames so subsequent calls in the same session see it.
 * Comparison is case-insensitive; the returned name preserves the original casing.
 * @param {string} baseName - Desired block name (without language suffix)
 * @param {string} language - Language code
 * @param {Set<string>} usedNames - Set of lowercased names already taken (mutated in place)
 * @returns {string} Unique parentBlockName (includes _lang suffix)
 */
const getUniqueBlockName = (baseName, language, usedNames) => {
  const candidate = `${baseName}_${language}`;
  const candidateLower = candidate.toLowerCase();
  if (!usedNames.has(candidateLower)) {
    usedNames.add(candidateLower);
    return candidate;
  }

  let suffix = 1;
  while (usedNames.has(`${baseName}_${suffix}_${language}`.toLowerCase())) {
    suffix += 1;
  }
  const unique = `${baseName}_${suffix}_${language}`;
  usedNames.add(unique.toLowerCase());
  return unique;
};

/**
 * Import XML blocks to standalone blocks
 * @param {string} xmlContent - XML file content as string
 * @param {number} resourceGroupId - Resource group ID (team ID)
 * @param {Function} onProgress - Progress callback function
 * @param {number} categoryId - Category ID
 * @param {boolean} excludeDuplicates - If true, skip blocks whose iytxtbs is in the duplicate list
 * @returns {Promise<Object>} Import result with statistics
 */
export const importXMLBlocks = async (xmlContent, resourceGroupId, onProgress, categoryId, excludeDuplicates = false) => {
  const result = {
    totalBlocks: 0,
    processedBlocks: 0,
    successfulImports: 0,
    failedImports: 0,
    blockStatuses: [],
    errors: [],
    failedBlocks: [],
    publishedBlocks: 0,
    qaReadyBlocks: 0,
    qaCompleteBlocks: 0,
    publishErrors: 0,
  };

  const failedBlockIndices = new Set();
  
  try {
    // Parse XML
    if (onProgress) {
      onProgress({
        stage: 'parsing',
        message: 'Parsing XML file...',
      });
    }
    
    const blocks = parseXMLBlocks(xmlContent);
    result.totalBlocks = blocks.length;
    
    if (onProgress) {
      onProgress({
        stage: 'parsing',
        message: `Found ${blocks.length} blocks to import`,
        totalBlocks: blocks.length,
      });
    }

    // Build name cache from existing published blocks + track session names
    const usedNames = await buildExistingBlockNameCache(onProgress);

    // Resolve block names: detects duplicate nameFr/nameNl groups (including
    // collisions with already-published names) and prepends povc where needed
    // so cross-povc blocks get distinct names.
    const resolvedNames = resolveBlockNames(blocks, usedNames);

    // Process a single block (all its languages) sequentially
    const processBlock = async (blockIndex) => {
      const block = blocks[blockIndex];
      const blockName = resolvedNames[blockIndex];
      const availableLanguages = getAvailableLanguages(block);
      
      if (availableLanguages.length === 0) {
        // Skip blocks with no valid language content
        result.processedBlocks++;
        return;
      }

      // Reject blocks that contain <img> or <table> in any language
      const hasUnsupportedTags = availableLanguages.some((lang) => {
        const decoded = getLanguageContent(block, lang);
        return decoded && containsUnsupportedTags(decoded);
      });
      if (hasUnsupportedTags) {
        const errorMessage = 'Block contains unsupported tags (table/img)';
        availableLanguages.forEach((language) => {
          const statusObj = {
            blockId: blockName,
            language,
            status: 'error',
            blockName,
            blockIndex,
            publishStatus: 'not-started',
            publishStage: null,
            publishErrorMessage: null,
            errorMessage,
          };
          result.blockStatuses.push(statusObj);
          result.failedImports++;
          result.errors.push({ block: blockName, language, error: errorMessage });
          result.processedBlocks++;
          if (onProgress) {
            onProgress({
              stage: 'importing',
              currentBlock: blockName,
              currentLanguage: language,
              blockIndex: blockIndex + 1,
              totalBlocks: blocks.length,
              processedBlocks: result.processedBlocks,
              successfulImports: result.successfulImports,
              failedImports: result.failedImports,
              statusObj,
            });
          }
        });
        failedBlockIndices.add(blockIndex);
        return;
      }

      // Optionally skip blocks with duplicate iytxtbs (only when block has non-empty iytxtbs)
      if (excludeDuplicates && block.iytxtbs && block.iytxtbs.trim() !== '' && DUPLICATE_IYTXTBS.has(block.iytxtbs.trim())) {
        const errorMessage = 'Duplicate iytxtbs -- skipped';
        availableLanguages.forEach((language) => {
          const statusObj = {
            blockId: blockName,
            language,
            status: 'error',
            blockName,
            blockIndex,
            publishStatus: 'not-started',
            publishStage: null,
            publishErrorMessage: null,
            errorMessage,
          };
          result.blockStatuses.push(statusObj);
          result.failedImports++;
          result.errors.push({ block: blockName, language, error: errorMessage });
          result.processedBlocks++;
          if (onProgress) {
            onProgress({
              stage: 'importing',
              currentBlock: blockName,
              currentLanguage: language,
              blockIndex: blockIndex + 1,
              totalBlocks: blocks.length,
              processedBlocks: result.processedBlocks,
              successfulImports: result.successfulImports,
              failedImports: result.failedImports,
              statusObj,
            });
          }
        });
        failedBlockIndices.add(blockIndex);
        return;
      }
      
      let groupId = '';
      // let firstInstanceBlockId;
      let firstLanguageSuccess = false;
      
      // Process each language for this block (sequentially to preserve groupId)
      for (let langIndex = 0; langIndex < availableLanguages.length; langIndex++) {
        const language = availableLanguages[langIndex];
        
        // Create status object
        const statusObj = {
          blockId: blockName,
          language,
          status: 'processing',
          blockName,
          blockIndex,
          publishStatus: 'not-started',
          publishStage: null,
          publishErrorMessage: null,
        };
        
        result.blockStatuses.push(statusObj);
        
        if (onProgress) {
          onProgress({
            stage: 'importing',
            currentBlock: blockName,
            currentLanguage: language,
            blockIndex: blockIndex + 1,
            totalBlocks: blocks.length,
            processedBlocks: result.processedBlocks,
            statusObj,
          });
        }
        
        try {
          // Create payload with a unique parentBlockName
          const uniqueParentName = getUniqueBlockName(blockName, language, usedNames);
          const payload = createBlockPayload(block, language, resourceGroupId, categoryId, groupId, uniqueParentName, blockName);
          
          // Call API (serialize new-group create + metadata so each block gets a distinct groupId)
          let response;
          // if (!firstLanguageSuccess) { // Create the standalone block for the first intance
            if (groupId) {
              response = await createStandaloneBlock(payload);
            } else {
              response = await runNewStandaloneGroup(async () => {
                const res = await createStandaloneBlock(payload);
                if (res.data?.id && res.data.groupId) {
                  const metadata = createMetadataPayload(block);
                  if (metadata.length > 0) {
                    await applyMetadataToImportedBlockGroup(
                      res.data.groupId,
                      metadata,
                      blockIndex,
                      blockName,
                    );
                  }
                }
                return res;
              });
            }
          /*} else { // Translate to retain group Id
            const translationResponse = await translateStandaloneBlock({ documentId: firstInstanceBlockId, languageCode: language });
            console.log("translateStandaloneBlock :: ", translationResponse);
            payload.parentBlockId = translationResponse.data?.id;
            payload.language = language;
            response = await patchComplexBlockDirect(payload, false);
            console.log({ response });
          }*/
          
          if (response.data?.id) {
            // Success
            statusObj.status = 'success';
            statusObj.blockId = response.data.id;
            result.successfulImports++;
            
            // Extract groupId from first language response (metadata already applied inside runNewStandaloneGroup)
            if (!firstLanguageSuccess) {
              groupId = response.data.groupId;
              firstLanguageSuccess = true;
            }

            // Publish the standalone block
            statusObj.publishStatus = 'processing';
            if (onProgress) {
              onProgress({
                stage: 'publishing',
                currentBlock: blockName,
                currentLanguage: language,
                blockIndex: blockIndex + 1,
                totalBlocks: blocks.length,
                statusObj: { ...statusObj, publishStatus: 'processing' },
              });
            }

            const publishResult = await publishBlock(response.data.id, (publishProgress) => {
              if (onProgress) {
                onProgress({
                  stage: 'publishing',
                  currentBlock: blockName,
                  currentLanguage: language,
                  blockIndex: blockIndex + 1,
                  totalBlocks: blocks.length,
                  statusObj: {
                    ...statusObj,
                    publishStatus: publishProgress.publishStatus || statusObj.publishStatus,
                    publishStage: publishProgress.publishStage || statusObj.publishStage,
                  },
                });
              }
            });

            // Update status with publishing result
            if (publishResult.success) {
              statusObj.publishStatus = 'published';
              statusObj.publishStage = 'published';
              result.publishedBlocks++;
            } else {
              statusObj.publishStatus = 'publish-error';
              statusObj.publishStage = publishResult.stage;
              statusObj.publishErrorMessage = publishResult.errorMessage;
              result.publishErrors++;
              
              // Track publishing stage for statistics
              if (publishResult.stage === 'qa-ready') {
                // Failed at QA Ready, so not counted
              } else if (publishResult.stage === 'qa-complete') {
                result.qaReadyBlocks++;
              } else if (publishResult.stage === 'published') {
                result.qaCompleteBlocks++;
              }
              
              result.errors.push({
                block: blockName,
                language,
                error: `Publishing failed: ${publishResult.errorMessage}`,
                type: 'publishing',
              });
              failedBlockIndices.add(blockIndex);
            }
          } else {
            // Error
            statusObj.status = 'error';
            failedBlockIndices.add(blockIndex);
            const error = response?.errors?.[0] || {};
            let errorMessage = error.description || 'Unknown error';
            
            if (error.code === 5601) {
              const unsupportedChars = extractUnsupportedCharacters(errorMessage);
              errorMessage = `Unsupported characters: ${unsupportedChars}`;
            }
            
            statusObj.errorMessage = errorMessage;
            result.failedImports++;
            result.errors.push({
              block: blockName,
              language,
              error: errorMessage,
            });
          }
        } catch (error) {
          // Network or other errors
          console.log("Error in trying to create standalone block :: ", error);
          statusObj.status = 'error';
          statusObj.errorMessage = error.message || 'Failed to import block';
          result.failedImports++;
          result.errors.push({
            block: blockName,
            language,
            error: error.message || 'Unknown error',
          });
          failedBlockIndices.add(blockIndex);
        }
        
        result.processedBlocks++;
        
        if (onProgress) {
          onProgress({
            stage: statusObj.publishStatus === 'processing' || statusObj.publishStatus === 'published' || statusObj.publishStatus === 'publish-error' 
              ? 'publishing' 
              : 'importing',
            currentBlock: blockName,
            currentLanguage: language,
            blockIndex: blockIndex + 1,
            totalBlocks: blocks.length,
            processedBlocks: result.processedBlocks,
            successfulImports: result.successfulImports,
            failedImports: result.failedImports,
            publishedBlocks: result.publishedBlocks,
            publishErrors: result.publishErrors,
            statusObj,
          });
        }
      }
    };

    // Process blocks concurrently (CONCURRENCY_LIMIT at a time)
    const blockIndices = Array.from({ length: blocks.length }, (_, i) => i);
    await processWithConcurrency(blockIndices, CONCURRENCY_LIMIT, processBlock);

    result.failedBlocks = blocks.filter((_, idx) => failedBlockIndices.has(idx));
    
    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: 'Import and publishing completed',
        totalBlocks: result.totalBlocks,
        processedBlocks: result.processedBlocks,
        successfulImports: result.successfulImports,
        failedImports: result.failedImports,
        publishedBlocks: result.publishedBlocks,
        qaReadyBlocks: result.qaReadyBlocks,
        qaCompleteBlocks: result.qaCompleteBlocks,
        publishErrors: result.publishErrors,
      });
    }
    
    return result;
  } catch (error) {
    console.error('Import error:', error);
    if (onProgress) {
      onProgress({
        stage: 'error',
        message: error.message || 'Failed to import blocks',
        error: error.message,
      });
    }
    throw error;
  }
};

